import type { IconButtonProps } from '@mui/material/IconButton';

import { m } from 'framer-motion';
import { usePopover } from 'minimal-shared/hooks';

import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';

import { useTranslate } from 'src/locales';

import { Iconify } from 'src/components/iconify';
import { CustomPopover } from 'src/components/custom-popover';
import { varTap, varHover, transitionTap } from 'src/components/animate';

// ----------------------------------------------------------------------

export type ContactSupportButtonProps = IconButtonProps & {
  phoneNumber?: string;
  whatsappNumber?: string;
};

export function ContactSupportButton({
  phoneNumber = '+917290991928',
  whatsappNumber = '+917290991928',
  sx,
  ...other
}: ContactSupportButtonProps) {
  const { t } = useTranslate('layout');

  const { open, anchorEl, onClose, onOpen } = usePopover();

  const handleCall = () => {
    window.location.href = `tel:${phoneNumber}`;
    onClose();
  };

  const handleWhatsApp = () => {
    window.open(`https://wa.me/${whatsappNumber.replace(/[^0-9]/g, '')}`, '_blank');
    onClose();
  };

  const renderMenuList = () => (
    <CustomPopover open={open} anchorEl={anchorEl} onClose={onClose}>
      <Typography variant="h6" sx={{ p: 1.5 }}>
        {t('contactSupport.title')}
      </Typography>

      <MenuList sx={{ width: 200 }}>
        <MenuItem onClick={handleCall} sx={{ py: 1.5 }}>
          <ListItemIcon sx={{ width: 'auto', mr: -2 }}>
            <Iconify icon="solar:phone-bold-duotone" width={24} />
          </ListItemIcon>
          <ListItemText
            primary={t('contactSupport.callUs')}
            secondary={phoneNumber}
            slotProps={{
              primary: {
                textAlign: 'left',
              },
              secondary: {
                textAlign: 'left',
                sx: { typography: 'caption', color: 'text.disabled' },
              },
            }}
          />
        </MenuItem>

        <MenuItem onClick={handleWhatsApp} sx={{ py: 1.5 }}>
          <ListItemIcon sx={{ width: 'auto', mr: -2 }}>
            <Iconify icon="logos:whatsapp-icon" width={24} />
          </ListItemIcon>
          <ListItemText
            primary={t('contactSupport.whatsapp')}
            secondary={whatsappNumber}
            slotProps={{
              secondary: {
                sx: { typography: 'caption', color: 'text.disabled' },
              },
            }}
          />
        </MenuItem>
      </MenuList>
    </CustomPopover>
  );

  return (
    <>
      <IconButton
        component={m.button}
        whileTap={varTap(0.96)}
        whileHover={varHover(1.04)}
        transition={transitionTap()}
        aria-label={t('contactSupport.ariaLabel')}
        onClick={onOpen}
        sx={[
          { p: 0, width: 40, height: 40 },
          (theme) => ({ ...(open && { bgcolor: theme.vars.palette.action.selected }) }),
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
        {...other}
      >
        <Iconify icon="solar:phone-calling-rounded-bold-duotone" width={24} />
      </IconButton>

      {renderMenuList()}
    </>
  );
}
